import React, { useEffect } from "react";
import { Form, Row, Col, Select, InputNumber, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

/* ===== LOV DEFINITIONS (FROM SHEET) ===== */

const yesNoLOV = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

export default function RiskRegulatoryBehaviourForm({
  form,
  initialValues,
  onFinish,
  durationMethodOptions,
  creditRiskClassOptions,
  exposureTypeOptions,
  productCategory, // 👈 add this
}) {
  const isDiscount = productCategory === 0; // T-Bill
  const isFixedIncome = productCategory === 1; // Bond
  const isMoneyMarket = productCategory === 2; // Interbank

  useEffect(() => {
    if (initialValues) form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  return (
    <>
      <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
        Risk & Regulatory Attributes
      </h3>

      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row gutter={24}>
          {/* Risk Weight — always required */}
          <Col span={12}>
            <Form.Item
              name="riskWeight"
              label={
                <span>
                  Risk Weight (%)
                  <Tooltip title="Used in capital adequacy and regulatory capital computations.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={100}
                style={{ width: "100%", height: 44 }}
                placeholder="0 – 100"
              />
            </Form.Item>
          </Col>

          {/* Duration Method — T-Bill optional, Bond mandatory, Interbank N/A */}
          {!isMoneyMarket && (
            <Col span={12}>
              <Form.Item
                name="durationMethod"
                label={
                  <span>
                    Duration Method
                    <Tooltip title="Determines how interest rate sensitivity is calculated.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                rules={isFixedIncome ? [{ required: true }] : []} // mandatory Bond, optional T-Bill
              >
                <Select
                  options={durationMethodOptions}
                  placeholder="Select Duration Method"
                  style={{ height: 44 }}
                />
              </Form.Item>
            </Col>
          )}

          {/* Exposure Type — always required */}
          <Col span={12}>
            <Form.Item
              name="exposureType"
              label="Exposure Type"
              rules={[{ required: true }]}
            >
              <Select
                options={exposureTypeOptions}
                placeholder="Select Exposure Type"
                style={{ height: 44 }}
              />
            </Form.Item>
          </Col>

          {/* Eligible for SLR — T-Bill mandatory, Bond optional, Interbank N/A */}
          {!isMoneyMarket && (
            <Col span={12}>
              <Form.Item
                name="eligibleForSlrFlag"
                label="Eligible for SLR"
                rules={isDiscount ? [{ required: true }] : []} // mandatory T-Bill, optional Bond
              >
                <Select
                  options={yesNoLOV}
                  placeholder="Yes / No"
                  style={{ height: 44 }}
                />
              </Form.Item>
            </Col>
          )}

          {/* Eligible for LCR — T-Bill mandatory, Bond optional, Interbank N/A */}
          {!isMoneyMarket && (
            <Col span={12}>
              <Form.Item
                name="eligibleForLcrFlag"
                label="Eligible for LCR"
                rules={isDiscount ? [{ required: true }] : []} // mandatory T-Bill, optional Bond
              >
                <Select
                  options={yesNoLOV}
                  placeholder="Yes / No"
                  style={{ height: 44 }}
                />
              </Form.Item>
            </Col>
          )}

          {/* Haircut Percentage — all optional */}
          <Col span={12}>
            <Form.Item
              name="haircutPercentage"
              label={
                <span>
                  Haircut Percentage (%)
                  <Tooltip title="Applied in collateral valuation and liquidity calculations.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
            >
              <InputNumber
                min={0}
                max={100}
                style={{ width: "100%", height: 44 }}
                placeholder="0 – 100"
              />
            </Form.Item>
          </Col>

          {/* Credit Risk Class — always required */}
          <Col span={12}>
            <Form.Item
              name="creditRiskClass"
              label="Credit Risk Class"
              rules={[{ required: true }]}
            >
              <Select
                options={creditRiskClassOptions}
                placeholder="Select Credit Risk Class"
                style={{ height: 44 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <button type="submit" hidden />
      </Form>
    </>
  );
}
